import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../lib/api';
import { SignatureModal } from '../components/SignatureModal';
import { SendDocumentModal } from '../components/SendDocumentModal';
import { UploadDocumentDialog } from '../components/UploadDocumentDialog';
import { DocumentInfoModal } from '../components/DocumentInfoModal';
import type { DocumentStatus, DocumentSummary, FolderSummary } from '../types';

const STATUS_CLASS: Record<DocumentStatus, string> = {
  draft: 'dtag-template',
  template: 'dtag-template',
  pending_signature: 'dtag-pending',
  signed: 'dtag-signed',
  archived: 'dtag-template',
};

// Mockup case-type tabs — area is a free-text column on Document, so these
// are the conventional values pre-filled by the AddClient flow. Values stay
// Hebrew (DB-compatible); labels come from the documents.areas namespace.
const AREA_TABS: { key: string | null; labelKey: string }[] = [
  { key: null, labelKey: 'areas.all' },
  { key: 'דיני משפחה', labelKey: 'areas.family' },
  { key: 'דיני עבודה', labelKey: 'areas.labor' },
  { key: 'נדל״ן', labelKey: 'areas.real_estate' },
  { key: 'מסחרי', labelKey: 'areas.commercial' },
  { key: 'ירושה', labelKey: 'areas.inheritance' },
];

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function extOf(name: string): { label: string; cls: string; emoji: string } {
  const m = /\.([a-z0-9]+)$/i.exec(name);
  const ext = m?.[1]?.toLowerCase() ?? '';
  if (ext === 'pdf') return { label: 'PDF', cls: 'dti-pdf', emoji: '📄' };
  if (ext === 'doc' || ext === 'docx') return { label: 'DOCX', cls: 'dti-doc', emoji: '📝' };
  if (ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'gif') {
    return { label: ext.toUpperCase(), cls: 'dti-jpg', emoji: '🖼' };
  }
  return { label: ext.toUpperCase() || 'FILE', cls: 'dti-doc', emoji: '📎' };
}

export function Documents({
  firmId,
  searchQuery,
}: {
  firmId: string;
  searchQuery: string;
}) {
  const { t } = useTranslation(['documents', 'common']);
  const [folders, setFolders] = useState<FolderSummary[]>([]);
  const [docs, setDocs] = useState<DocumentSummary[]>([]);
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null); // null = all
  const [activeArea, setActiveArea] = useState<string | null>(null); // null = all
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Document targeted for signature send (modal trigger)
  const [signingDoc, setSigningDoc] = useState<DocumentSummary | null>(null);
  const [sendingDoc, setSendingDoc] = useState<DocumentSummary | null>(null);
  const [infoDoc, setInfoDoc] = useState<DocumentSummary | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  // Captured from the pre-upload dialog and applied to the file when picked.
  const pendingUploadTarget = useRef<{ clientId: string; caseId: string | null } | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (activeFolderId) params.set('folderId', activeFolderId);
      if (activeArea) params.set('area', activeArea);
      const [foldersRes, docsRes] = await Promise.all([
        apiFetch<{ items: FolderSummary[] }>('/api/folders'),
        apiFetch<{ items: DocumentSummary[] }>(`/api/documents?${params}`),
      ]);
      setFolders(foldersRes.items);
      setDocs(docsRes.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFolderId, activeArea]);

  // Local search overlay — server returns the filtered set per folder/area;
  // the topbar query then narrows further by name/area substring.
  const filteredDocs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return docs;
    return docs.filter(
      (d) =>
        d.name.toLowerCase().includes(q) || (d.area ?? '').toLowerCase().includes(q),
    );
  }, [docs, searchQuery]);

  async function newFolder() {
    const name = prompt(t('folders.prompt_name'));
    if (!name?.trim()) return;
    try {
      const folder = await apiFetch<FolderSummary>('/api/folders', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim() }),
      });
      setFolders((prev) => [...prev, folder]);
    } catch (e) {
      alert(t('common:errors.with_message', { message: e instanceof Error ? e.message : t('common:errors.unknown') }));
    }
  }

  // Step 1 of upload: open the pre-dialog so the user picks client + case
  // before we touch their filesystem. The actual file picker fires on
  // dialog confirm.
  function handleUploadClick() {
    setUploadDialogOpen(true);
  }

  function handleUploadConfirm(opts: { clientId: string; caseId: string | null }) {
    pendingUploadTarget.current = opts;
    setUploadDialogOpen(false);
    // Defer to next tick so the modal animates out before the OS file
    // picker steals focus.
    setTimeout(() => fileInputRef.current?.click(), 0);
  }

  async function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // reset so the same file can be re-picked
    const target = pendingUploadTarget.current;
    pendingUploadTarget.current = null;
    if (!target) {
      // Shouldn't happen — pre-dialog must run first. Fall back gracefully.
      setError('upload_target_missing');
      return;
    }

    setUploading(true);
    setError(null);
    try {
      // Step 1: ask the API for a presigned upload URL
      const pres = await apiFetch<{
        documentId: string;
        version: number;
        key: string;
        uploadUrl: string;
      }>('/api/documents/upload-url', {
        method: 'POST',
        body: JSON.stringify({
          name: file.name,
          mime: file.type || 'application/octet-stream',
          size: file.size,
          caseId: target.caseId,
        }),
      });

      // Step 2: PUT the bytes directly to R2
      const putRes = await fetch(pres.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!putRes.ok) {
        throw new Error(`R2 upload failed (${putRes.status})`);
      }

      // Step 3: persist the metadata
      await apiFetch<DocumentSummary>('/api/documents', {
        method: 'POST',
        body: JSON.stringify({
          id: pres.documentId,
          name: file.name,
          mime: file.type || 'application/octet-stream',
          size: file.size,
          r2Key: pres.key,
          folderId: activeFolderId,
          area: activeArea,
          caseId: target.caseId,
        }),
      });

      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.code : err instanceof Error ? err.message : 'upload_failed');
    } finally {
      setUploading(false);
    }
  }

  async function preview(doc: DocumentSummary) {
    try {
      const res = await apiFetch<{ url: string }>(`/api/documents/${doc.id}/download-url`);
      // Open in a new tab — browser handles PDF/image inline, others get downloaded
      window.open(res.url, '_blank', 'noopener');
    } catch (e) {
      alert(
        t('common:errors.document_open_failed', {
          message: e instanceof Error ? e.message : t('common:errors.unknown'),
        }),
      );
    }
  }

  async function deleteDoc(doc: DocumentSummary) {
    if (!confirm(t('actions.delete_confirm', { name: doc.name }))) return;
    try {
      await apiFetch(`/api/documents/${doc.id}`, { method: 'DELETE' });
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (e) {
      alert(t('common:errors.with_message', { message: e instanceof Error ? e.message : t('common:errors.unknown') }));
    }
  }

  /**
   * Pull the current envelope status from DocuSign and apply it. Useful as
   * a manual fallback when Connect isn't configured or a webhook was
   * dropped. Looks up the most recent envelope for the document and syncs.
   */
  async function syncSignatureStatus(doc: DocumentSummary) {
    try {
      const list = await apiFetch<{ items: Array<{ id: string }> }>(
        `/api/documents/${doc.id}/signatures`,
      );
      const envelope = list.items[0];
      if (!envelope) return;
      await apiFetch(`/api/signatures/${envelope.id}/sync`, { method: 'POST' });
      await load();
    } catch (e) {
      alert(t('common:errors.with_message', { message: e instanceof Error ? e.message : t('common:errors.unknown') }));
    }
  }

  // Top-level folders (parentId === null). Real tree rendering is one
  // level deep for now — matches mockup. Subfolders to follow if needed.
  const topFolders = folders.filter((f) => f.parentId === null);
  const totalDocCount = folders.reduce((sum, f) => sum + f.documentCount, 0);

  return (
    <div>
      <div className="docs-toolbar">
        <div className="docs-title">{t('toolbar.title')}</div>
        <button className="docs-btn" onClick={handleUploadClick} disabled={uploading}>
          {uploading ? t('toolbar.uploading') : t('toolbar.upload')}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileSelected}
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        />
      </div>

      <div className="case-tabs">
        {AREA_TABS.map((tab) => (
          <div
            key={tab.labelKey}
            className={`case-tab ${activeArea === tab.key ? 'active' : ''}`}
            onClick={() => setActiveArea(tab.key)}
          >
            {t(tab.labelKey)}
          </div>
        ))}
      </div>

      {error && <div className="signin-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="docs-layout">
        <div className="folder-panel">
          <div className="folder-header">
            {t('folders.heading')}
            <span className="folder-add" onClick={newFolder} title={t('folders.new_title')}>+</span>
          </div>
          <div
            className={`folder-item ${activeFolderId === null ? 'active' : ''}`}
            onClick={() => setActiveFolderId(null)}
          >
            <div className="folder-icon">🗂️</div>
            <div className="folder-info">
              <div className="folder-name">{t('folders.all')}</div>
              <div className="folder-count">{t('folders.files_count', { count: totalDocCount })}</div>
            </div>
          </div>
          {topFolders.map((f) => (
            <div
              key={f.id}
              className={`folder-item ${activeFolderId === f.id ? 'active' : ''}`}
              onClick={() => setActiveFolderId(f.id)}
            >
              <div className="folder-icon">{f.icon ?? '📁'}</div>
              <div className="folder-info">
                <div className="folder-name">{f.name}</div>
                <div className="folder-count">{t('folders.files_count', { count: f.documentCount })}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="doc-list-panel">
          <div className="doc-list-header">
            <div>
              <div className="doc-list-title">
                {activeFolderId
                  ? folders.find((f) => f.id === activeFolderId)?.name ?? t('folders.default_name')
                  : t('list.title_all')}
              </div>
              <div className="doc-list-sub">
                {t('list.count', { count: filteredDocs.length })}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="empty">{t('list.loading')}</div>
          ) : filteredDocs.length === 0 ? (
            <div className="empty">
              {searchQuery.trim() ? t('list.empty_search') : t('list.empty')}
            </div>
          ) : (
            filteredDocs.map((d) => {
              const ext = extOf(d.name);
              const ownerLabel = d.case
                ? [d.case.client?.name, d.case.caseNumber].filter(Boolean).join(' · ')
                : null;
              return (
                <div key={d.id} className="doc-item">
                  <div
                    className={`doc-type-icon ${ext.cls}`}
                    onClick={() => setInfoDoc(d)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="dti-icon-bg">{ext.emoji}</div>
                    {ext.label}
                  </div>
                  <div
                    className="doc-info"
                    onClick={() => setInfoDoc(d)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="doc-name">{d.name}</div>
                    <div className="doc-meta">
                      {ownerLabel && (
                        <span style={{ color: 'var(--accent-mid)', fontWeight: 500 }}>
                          {ownerLabel}
                        </span>
                      )}
                      {d.area && <span>{d.area}</span>}
                      <span>{d.updatedAt.slice(0, 10)}</span>
                      {d.latestVersion && <span>{formatBytes(d.latestVersion.size)}</span>}
                    </div>
                    <span className={`doc-tag ${STATUS_CLASS[d.status]}`}>
                      {t(`statuses.${d.status}`)}
                    </span>
                  </div>
                  <div className="doc-actions">
                    <button className="doc-action-btn btn-preview" onClick={() => preview(d)}>
                      {t('actions.preview')}
                    </button>
                    <button
                      className="doc-action-btn"
                      onClick={() => setSendingDoc(d)}
                      style={{ color: 'var(--accent-mid)' }}
                    >
                      {t('actions.send', { defaultValue: 'Send' })}
                    </button>
                    {/* Hide "send for signature" if the doc is already in a
                        terminal post-signature state. */}
                    {d.status !== 'signed' && (
                      <button
                        className="doc-action-btn"
                        onClick={() => setSigningDoc(d)}
                        style={{ color: 'var(--accent)' }}
                      >
                        {t('signatures:send_for_signature', { defaultValue: 'Send for signature' })}
                      </button>
                    )}
                    {d.status === 'pending_signature' && (
                      <button
                        className="doc-action-btn"
                        onClick={() => syncSignatureStatus(d)}
                        title="Pull current envelope state from DocuSign"
                      >
                        ↻
                      </button>
                    )}
                    <button className="doc-action-btn" onClick={() => deleteDoc(d)} style={{ color: 'var(--red)' }}>
                      {t('actions.delete')}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {signingDoc && (
        <SignatureModal
          document={signingDoc}
          onSent={() => {
            setSigningDoc(null);
            void load();
          }}
          onClose={() => setSigningDoc(null)}
        />
      )}

      {sendingDoc && (
        <SendDocumentModal
          document={sendingDoc}
          firmId={firmId}
          onSent={() => setSendingDoc(null)}
          onClose={() => setSendingDoc(null)}
        />
      )}

      {uploadDialogOpen && (
        <UploadDocumentDialog
          onConfirm={handleUploadConfirm}
          onClose={() => setUploadDialogOpen(false)}
        />
      )}

      {infoDoc && (
        <DocumentInfoModal
          document={infoDoc}
          onClose={() => setInfoDoc(null)}
        />
      )}
    </div>
  );
}
